import { Copy, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button, Card, Flex, HeaderItalic, Progress, Text, TextField } from '@/components/common';
import { analyticsClient, appUrl, cn } from '@/lib';

interface InviteCodeProps {
  code: string;
  used?: number;
  total?: number;
}

const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
const canWriteClipboard =
  typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function';

export default function InviteCode({ code, used, total }: InviteCodeProps) {
  const { t } = useTranslation('profile');
  const exhausted = used != null && total != null && used >= total;

  const handleCopy = async () => {
    try {
      if (canWriteClipboard) {
        await navigator.clipboard.writeText(code);
      } else {
        // Fallback for environments without Clipboard API
        const el = document.createElement('textarea');
        el.value = code;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        const success = document.execCommand('copy');
        document.body.removeChild(el);
        if (!success) throw new Error('execCommand copy failed');
      }
      analyticsClient.capture('invite_code_copied');
      toast.success(t('edit.inviteCode.copied'));
    } catch (err) {
      console.error('Failed to copy invite code:', err);
      toast.error(t('edit.inviteCode.copyFailed'));
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: t('edit.inviteCode.shareTitle'),
        text: t('edit.inviteCode.shareMessage', { code, url: appUrl }),
      });
      // navigator.share resolves when the share sheet is dismissed or a target is chosen;
      // treat resolution as "initiated" rather than "completed".
      analyticsClient.capture('invite_code_share_initiated');
      toast.success(t('edit.inviteCode.shared'));
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('Failed to share invite code:', err);
        toast.error(t('edit.inviteCode.shareFailed'));
      }
    }
  };

  return (
    <Card>
      <Flex direction="column" gap="3">
        <HeaderItalic color="white">{t('edit.fields.inviteCode')}</HeaderItalic>
        <Text as="p" size="1" color="gray">
          {t('edit.inviteCode.description')}
        </Text>
        <Flex align="center" gap="2" mt="2">
          <TextField.Root
            id="inviteCode"
            disabled
            value={code}
            className={cn('font-mono!', !exhausted && 'input-accent')}
          ></TextField.Root>
          <Button
            type="button"
            variant="outline"
            color="gray"
            highContrast
            radius="full"
            size="icon"
            icon={<Copy />}
            disabled={exhausted}
            onClick={handleCopy}
            aria-label={t('edit.inviteCode.copyAria')}
          />
          {canShare && (
            <Button
              type="button"
              variant="outline"
              color="gray"
              highContrast
              radius="full"
              size="icon"
              icon={<Share2 />}
              disabled={exhausted}
              onClick={handleShare}
              aria-label={t('edit.inviteCode.shareAria')}
            />
          )}
        </Flex>
        {used != null && total != null && total > 0 && (
          <Flex direction="column" gap="1">
            <Text as="p" size="1" color="gray">
              {t('edit.inviteCode.uses', { used, total })}
            </Text>
            <Progress value={used} max={total} size="2" color={used >= total ? 'gray' : 'red'} />
          </Flex>
        )}
      </Flex>
    </Card>
  );
}
